import { motion } from "framer-motion";
import { ArrowRight, CalendarDots, ShieldCheck, Lightning, Clock, MapPin } from "@phosphor-icons/react";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-lumos-bg"
        >
            {/* subtle architectural background */}
            <div className="absolute inset-0 bg-grid opacity-60" />
            <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-lumos-blue/8 blur-3xl" />
            <div className="absolute -bottom-32 -left-10 w-[460px] h-[460px] rounded-full bg-lumos-green/8 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                {/* LEFT — Copy */}
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="flex items-center gap-3 mb-7"
                    >
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lumos-green/10 border border-lumos-green/30 text-lumos-green text-xs font-bold tracking-wider uppercase shine-badge">
                            <ShieldCheck size={14} weight="fill" /> Certifié RGE
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lumos-blue/8 border border-lumos-blue/25 text-lumos-blue text-xs font-bold tracking-wider uppercase">
                            <Lightning size={14} weight="fill" /> Depuis 2022
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        data-testid="hero-title"
                        className="font-display text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] font-extrabold text-lumos-ink tracking-tight"
                    >
                        Votre <span className="text-lumos-blue">audit énergétique</span>
                        <span className="block mt-2">
                            en moins de{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10 text-lumos-amber gold-underline">48h</span>
                            </span>
                            .
                        </span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="mt-7 text-lg lg:text-xl text-lumos-muted max-w-2xl leading-relaxed"
                    >
                        Maison, copropriété ou immeuble :<br className="hidden sm:block" />
                        diagnostics et audits réalisés rapidement par des experts certifiés RGE.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="mt-9 flex flex-col sm:flex-row gap-4"
                    >
                        <a href="#contact" data-testid="hero-cta-quote" className="btn-primary">
                            Obtenir mon devis
                            <ArrowRight size={18} weight="bold" />
                        </a>
                        <a href="#contact" data-testid="hero-cta-appointment" className="btn-secondary">
                            <CalendarDots size={18} weight="bold" />
                            Prendre rendez-vous
                        </a>
                    </motion.div>

                    {/* trust line */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 0.45 }}
                        className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-lumos-muted"
                    >
                        <span className="flex items-center gap-2"><Clock size={16} weight="bold" className="text-lumos-blue" /> Rapport sous 24-48h</span>
                        <span className="flex items-center gap-2"><MapPin size={16} weight="bold" className="text-lumos-blue" /> Île-de-France · Oise · Aisne</span>
                        <span className="flex items-center gap-2"><Lightning size={16} weight="bold" className="text-lumos-blue" /> Audit maison dès 500€ TTC</span>
                    </motion.div>
                </div>

                {/* RIGHT — Architectural visual */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    className="lg:col-span-5 relative"
                >
                    <HeroVisual />
                </motion.div>
            </div>
        </section>
    );
}

function HeroVisual() {
    return (
        <div className="relative w-full aspect-[4/5] rounded-[28px] overflow-hidden border border-lumos-border shadow-[0_30px_80px_-25px_rgba(10,17,40,0.35)]">
            {/* base architectural image */}
            <img
                src="https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGZhY2FkZSUyMGNsZWFufGVufDB8fHx8MTc4MTU0NTg1N3ww&ixlib=rb-4.1.0&q=85"
                alt="Façade architecturale moderne — audit énergétique LUMOS"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
            />
            {/* dark gradient overlay to make it premium */}
            <div className="absolute inset-0 bg-gradient-to-t from-lumos-midnight/85 via-lumos-midnight/30 to-transparent" />

            {/* thermography scan line */}
            <div className="absolute inset-x-0 top-0 h-24 thermo-overlay animate-thermo-scan pointer-events-none" />

            {/* corner badge "thermal" */}
            <div className="absolute top-5 left-5 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-white text-[11px] font-semibold tracking-wider uppercase">
                <span className="w-2 h-2 rounded-full bg-lumos-green animate-pulse" />
                Analyse thermique en cours
            </div>

            {/* floating data points */}
            <FloatingPoint top="22%" left="32%" label="-1.4°C" tone="cool" />
            <FloatingPoint top="48%" left="68%" label="+3.2°C" tone="warm" delay="1.5s" />
            <FloatingPoint top="72%" left="22%" label="Pertes" tone="alert" delay="0.7s" />

            {/* bottom info card */}
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 backdrop-blur-xl border border-white/60 p-5 flex items-center gap-4 shadow-xl">
                <div className="w-12 h-12 rounded-xl bg-lumos-blue/10 grid place-items-center text-lumos-blue">
                    <Lightning size={24} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-bold tracking-wider uppercase text-lumos-blue">Étiquette estimée</div>
                    <div className="font-display text-2xl font-extrabold text-lumos-ink leading-none mt-0.5">Classe D → B</div>
                </div>
                <div className="flex flex-col gap-1">
                    {["A", "B", "C", "D"].map((c, i) => (
                        <div
                            key={c}
                            className="h-1.5 rounded-full"
                            style={{
                                width: `${24 + i * 6}px`,
                                background: ["#22C55E", "#A3E635", "#FACC15", "#F59E0B"][i],
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function FloatingPoint({ top, left, label, tone, delay = "0s" }) {
    const colors = {
        cool: "bg-lumos-blue text-white shadow-[0_0_24px_rgba(0,175,255,0.55)]",
        warm: "bg-lumos-amber text-white shadow-[0_0_24px_rgba(245,158,11,0.55)]",
        alert: "bg-rose-500 text-white shadow-[0_0_24px_rgba(244,63,94,0.55)]",
    };
    return (
        <div
            className="absolute"
            style={{ top, left, animationDelay: delay }}
        >
            <div className="relative animate-float-y" style={{ animationDelay: delay }}>
                <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${colors[tone]} animate-pulse-glow`}>{label}</div>
            </div>
        </div>
    );
}
